<template>
	<v-container class="mb-0 pb-0 single-client-view">
		<v-card elevation="0" outlined class="single-client-section">
			<v-card-text class="text-center pa-6">
				<v-avatar color="grey lighten-2" size="150" class="mb-4">
					<v-img v-if="logoURL" width="300" :src="logoURL" />
					<v-icon v-else size="75">fas fa-building</v-icon>
				</v-avatar>
				<h1 class="relationship-title mb-4">
					Your relationship with {{ displayName }}
				</h1>
				<div
					class="connection-status-banner white--text py-3 px-4"
					:class="connectionStatusBannerClass"
				>
					{{ connectionStatusString(displayName) }}
				</div>
			</v-card-text>
		</v-card>

		<template v-if="connectionStatus && connectionStatus != 'none'">
			<div class="section-break"></div>
			<v-card elevation="0" outlined class="single-client-section">
				<v-card-text class="pa-4 pt-5 pb-4">
					<connection-sustainability-score
						:score1="getScore(1)"
						:score2="getScore(2)"
						:score3="getScore(3)"
						:score4="getScore(4)"
						:title="`Sustainability Score for ${displayName}`"
					/>
				</v-card-text>
			</v-card>
			<div class="section-break"></div>
			<template v-if="connectionStatus === 'mutual'">
				<div id="contract-status-section" ref="contractStatusSection">
					<contract-status
						ref="contractStatus"
						:contracts="contracts"
						:uploading="contractUploading"
						:kpi-evaluation="kpiEvaluation"
						:kpi-saving="kpiSaving"
						:can-view-kpi="canViewKpi"
						:can-enter-kpi="canEnterKpi"
						:can-upload-contract="canUploadContract"
						@upload="uploadContract"
						@upload-error="showContractUploadError"
						@download="downloadContract"
						@kpi-evaluation="saveKpiEvaluation"
						@kpi-access-denied="showKpiAccessDenied"
					/>
				</div>
				<div class="section-break"></div>
				<div id="supplier-performance-section" ref="supplierPerformanceSection">
					<supplier-performance
						v-if="canViewKpi"
						:has-contract="contracts.length > 0"
						:kpi-evaluation="kpiEvaluation"
						:kpi-history="kpiEvaluationHistory"
						:can-enter-kpi="canEnterKpi"
						@edit-kpi="openKpiEvaluation"
					/>
				</div>
				<div class="section-break"></div>
			</template>
			<v-card elevation="0" outlined class="single-client-section">
				<v-card-text class="pa-6">
					<v-row align="start" class="activity-stats-row ma-0">
						<v-col cols="12" class="pa-0 pb-3 flex-grow-0">
							<v-menu offset-y>
								<template v-slot:activator="{ on, attrs }">
									<v-chip label color="primary" dark v-bind="attrs" v-on="on">
										<span>Last <b>{{ selectedDayCount }}</b> days</span>
									</v-chip>
								</template>
								<v-list>
									<v-list-item-group @change="dayChange">
										<v-list-item v-for="(item, index) in items" :key="index" :value="item">
											<v-list-item-title>{{ item }}</v-list-item-title>
										</v-list-item>
									</v-list-item-group>
								</v-list>
							</v-menu>
						</v-col>
						<v-col cols="12" sm="6" class="pa-0 pr-sm-2 pb-2 pb-sm-0">
							<v-simple-table class="activity-table" dense>
								<template v-slot:default>
									<tbody>
										<tr>
											<td>
												Audits completed last
												<strong>{{ selectedDayCount }}</strong>
												days
											</td>
											<td class="activity-value">{{ auditsCompleted }}</td>
										</tr>
										<tr>
											<td>
												Audits received last
												<strong>{{ selectedDayCount }}</strong>
												days
											</td>
											<td class="activity-value">{{ auditsReceived }}</td>
										</tr>
									</tbody>
								</template>
							</v-simple-table>
						</v-col>
						<v-col cols="12" sm="6" class="pa-0 pl-sm-2">
							<v-simple-table class="activity-table" dense>
								<template v-slot:default>
									<tbody>
										<tr>
											<td>
												Transactions sent last
												<strong>{{ selectedDayCount }}</strong>
												days
											</td>
											<td class="activity-value">{{ transactionsSent }}</td>
										</tr>
										<tr>
											<td>
												Transactions received last
												<strong>{{ selectedDayCount }}</strong>
												days
											</td>
											<td class="activity-value">{{ transactionsReceived }}</td>
										</tr>
									</tbody>
								</template>
							</v-simple-table>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
			<div class="section-break"></div>
			<v-card elevation="0" outlined class="single-client-section">
				<v-card-text class="pa-6 pt-8">
					<h3 class="section-title darkColor--text mb-4">Recent Audits</h3>
					<v-data-table
						:loading="loading"
						light
						:headers="auditHeaders"
						:items="auditItems"
						:items-per-page="5"
						:footer-props="{ 'items-per-page-options': [5, 10, 15] }"
						@click:row="auditRowClicked"
					>
						<template v-slot:item.template="{ item }">
							{{ item.template }}
							<v-tooltip top v-if="item.flowDown == 1">
								<template v-slot:activator="{ on, attrs }">
									<v-icon 
										color="primary" 
										class="mr-3"
										small 
										v-on="on" 
										v-bind="attrs"
									>
										fas fa-turn-down
									</v-icon>
								</template>
								<span>Flow-Down</span>
							</v-tooltip>
							<v-tooltip top v-else-if="item.onsite == 1">
								<template v-slot:activator="{ on, attrs }">
									<v-icon 
										color="primary" 
										class="mr-3"
										small 
										v-on="on" 
										v-bind="attrs"
									>
										fas fa-building
									</v-icon>
								</template>
								<span>Onsite</span>
							</v-tooltip>
						</template>
						<template v-slot:item.sender="{ item }">
							<v-tooltip top v-if="item.onsite == 1">
								<template v-slot:activator="{ on, attrs }">
									<span v-on="on" v-bind="attrs">{{ item.sender }}</span>
								</template>
								<span>{{ item.auditorType == 1 ? 'Internal Onsite-Auditor' : 'External Onsite-Auditor' }}</span>
							</v-tooltip>
							<span v-else>{{ item.sender }}</span>
						</template>
						<template v-slot:item.assess="{ item }">
							<v-btn v-if="item.onsite !== 1" :to="'/audit/audits/' + item.id" color="secondary" label small outlined>
								{{ item.assess }}
								{{ $accessToOperation('AU', 'U') ? "Assess" : "View" }}
								<v-icon right small>fas fa-tasks</v-icon>
							</v-btn>
							<v-btn v-else :to="'/auditonsite/onsiteaudits/' + item.id" color="secondary" label small outlined>
								{{ item.assess }}
								{{ $accessToOperation('OAU', 'U') ? "Assess" : "View" }}
								<v-icon right small>fas fa-tasks</v-icon>
							</v-btn>
						</template>
						<template v-slot:item.click="{ item }">
							<v-btn v-if="item.onsite !== 1" :color="getButtonColor(item.status)" icon :to="{ name: `Audit Details`, params: { id: item.id } }"><v-icon>fas
									fa-arrow-circle-right</v-icon></v-btn>
							<v-btn v-else :color="getButtonColor(item.status)" icon :to="{ name: `Audit Onsite Details`, params: { id: item.id } }"><v-icon>fas
								fa-arrow-circle-right</v-icon></v-btn>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
			<div class="section-break"></div>
			<v-card elevation="0" outlined class="single-client-section">
				<v-card-text class="pa-6 pt-8">
					<h3 class="section-title darkColor--text mb-4">Recent Transactions</h3>
					<v-data-table
						light
						:headers="headers"
						:items="recentTransactions"
						:items-per-page="10"
						class="assessed-table"
						:footer-props="{ 'items-per-page-options': [5, 10, 15] }"
						@click:row="transactionRowClicked"
					>
						<template v-slot:item.id="{ item }">
							<router-link
								:to="{ name: `Transaction Details`, params: { id: item.id } }">{{ item.id }}</router-link>
						</template>
						<template v-slot:item.timestamp="{ item }">
							{{ item.timestamp | moment("DD/MM/YYYY HH:mm:ss") }}
						</template>
						<template v-slot:item.status="{ item }">
							<v-tooltip top>
								<template v-slot:activator="{ on, attrs }">
									<p class="mx-2 my-0" :style="{ color: item.status.color }" v-bind="attrs" v-on="on">
										<v-icon small :style="{ color: item.status.color }">fas fa-{{
							item.status.icon }}</v-icon>
										{{ item.status.description_short }}
									</p>
								</template>
								<span>{{ item.status.description_long }}</span>
							</v-tooltip>
						</template>
						<template v-slot:item.blockchainStatus="{ item }">
							<a v-if="item.block !== 'Awaiting validification'"
								:href="'http://nodes.orixe.tech/blocks/' + item.block" target='_blank'>Block
								#{{ item.block }}</a>
							<p class="my-0 text-left font-italic" v-if="item.block === 'Awaiting validification'">
								{{ item.block }}</p>
						</template>
						<template v-slot:item.click="{ item }">
							<v-btn color="trace" icon
								:to="{ name: `Transaction Details`, params: { id: item.id } }"><v-icon>fas
									fa-arrow-circle-right</v-icon></v-btn>
						</template>
				</v-data-table>
			</v-card-text>
		</v-card>
		</template>
		<div class="section-break"></div>
		<v-card elevation="0" outlined class="single-client-section">
			<v-card-text class="pa-6 pt-8">
				<h2 class="section-title darkColor--text mb-2">Company Policy Update History</h2>
				<p class="section-subtitle mb-4">You can see your company policy updated history in here.</p>
				<v-data-table
					light
					:headers="policyHeaders"
					:items="policies"
					class="completed-table"
					hide-default-footer
				>
					<template v-slot:item.download="{ item }">
						<v-btn color="primary"
							label
							small
							elevation="2"
							@click="downloadFiles(item.fileID, item.filename)">
							<v-icon left small>fas fa-download</v-icon>
							Download
						</v-btn>
					</template>
				</v-data-table>
			</v-card-text>
		</v-card>
		<div class="section-break"></div>
		<v-card elevation="0" outlined class="single-client-section">
			<v-card-text class="pa-6 pt-8">
				<h2 class="section-title darkColor--text mb-2">Company Certifications Update History</h2>
				<p class="section-subtitle mb-4">You can see your company certification updated history in here.</p>
				<v-data-table
					light
					:headers="certificationHeaders"
					:items="certifications"
					class="completed-table"
					hide-default-footer
				>
					<template v-slot:item.download="{ item }">
						<v-btn color="#325593"
							style="color: white;"
							label
							small
							elevation="2"
							@click="downloadFiles(item.fileID, item.filename)">
							<v-icon left small>fas fa-download</v-icon>
							Download
						</v-btn>
					</template>
				</v-data-table>
			</v-card-text>
		</v-card>

		<kpi-performance-summary-dialog
			v-model="showKpiPerformanceDialog"
			:evaluation="kpiPerformanceSummary"
			:evaluator-name="displayName"
		/>
	</v-container>
</template>


<script>
import LoadingState from "../../consts/LoadingState"
import AuditsAPI from "../../api/audit/audit"
import TransactionAPI from "../../api/trace/transaction"
import ConnectionsAPI from "../../api/connections/connection"
import SupplierAPI from "../../api/csr/supplier"
import FilesAPI from "../../api/files/files"
import ContractStatus from "../../components/ContractStatus.vue"
import SupplierPerformance from "../../components/SupplierPerformance.vue"
import KpiPerformanceSummaryDialog from "../../components/KpiPerformanceSummaryDialog.vue"
import ConnectionSustainabilityScore from "../../components/ConnectionSustainabilityScore.vue"
import { canEnterKpiFromRoles, canUploadContractFromRoles } from "../../utils/kpiAccess"

export default {
	name: "SingleClientView",
	components: { ContractStatus, SupplierPerformance, KpiPerformanceSummaryDialog, ConnectionSustainabilityScore },
	data() {
		return {
			policies: [],
			certifications: [],
			policyFile: null,
			supplier: {
				csr: null
			},
			recentOptions: {
				limit: 5,
				offset: 0,
				total: 0,
				data: [],
				loading: true,
				options: {},
			},
			auditAccess: false,
			traceAccess: false,
			recentTransactions: [],
			auditsCompleted: null,
			auditsReceived: null,
			transactionsSent: null,
			transactionsReceived: null,
			selectedDayCount: 180,
			items: [30, 60, 90, 180],
			otherClient: 0,
			connectionStatus: null,
			name: null,
			loading: false,
			headers: [
				{
					text: "Transaction ID",
					align: "start",
					sortable: false,
					value: "id",
					class: "secondary--text"
				},
				{
					text: "Sender",
					value: "sender",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "Recipient",
					value: "recipient",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "Timestamp",
					value: "timestamp",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "Status",
					value: "status",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "Blockchain Status",
					value: "blockchainStatus",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "View",
					value: "click",
					sortable: false,
					class: "secondary--text"
				}
			],
			auditHeaders: [
				{
					text: "Audit Template",
					value: "template",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "Sender",
					value: "sender",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "Recipient",
					value: "recipient",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "Timestamp",
					value: "timestamp",
					sortable: false,
					class: "secondary--text"
				},
				{
					text: "View",
					value: "click",
					sortable: false,
					class: "secondary--text"
				}
			],
			policyHeaders: [
				{
					text: "Policy Name",
					value: "filename",
					sortable: false,
					width: "40%",
					class: "secondary--text"
				},
				{
					text: "Description",
					value: "shortDescription",
					sortable: false,
					width: "40%",
					class: "secondary--text"
				},
				{
					text: "Download",
					value: "download",
					sortable: false,
					width: "20%",
					class: "secondary--text"
				}
			],
			certificationHeaders: [
				{
					text: "Certification Name",
					value: "filename",
					sortable: false,
					width: "40%",
					class: "secondary--text"
				},
				{
					text: "Description",
					value: "shortDescription",
					sortable: false,
					width: "40%",
					class: "secondary--text"
				},
				{
					text: "Download",
					value: "download",
					sortable: false,
					width: "20%",
					class: "secondary--text"
				}
			],
			incomingAudits: [],
			outgoingAudits: [],
			completedaudits_options: {
				itemsPerPage: 10
			},
			completedaudits_total: 0,
			logoURL: null,
			flowDown: null,
			incomingOnsiteAudits: [],
			onsite: null,
			contracts: [],
			contractUploading: false,
			kpiEvaluation: null,
			kpiEvaluationHistory: [],
			kpiSaving: false,
			showKpiPerformanceDialog: false,
			kpiPerformanceSummary: null,
		}
	},
	computed: {
		auditItems() {
			const allAudits = [
				...this.incomingAudits,
				...this.outgoingAudits,
				...this.incomingOnsiteAudits
			]

			return allAudits.sort((a, b) => {
				const dateA = this.$moment(a.timestamp, "DD/MM/YYYY HH:mm:ss").toDate()
				const dateB = this.$moment(b.timestamp, "DD/MM/YYYY HH:mm:ss").toDate()

				return dateB - dateA
			})
		},
		connectionStatusBannerClass() {
			switch (this.connectionStatus) {
			case "mutual":
				return "connection-status-banner--success"
			case "none":
				return "connection-status-banner--error"
			default:
				return "connection-status-banner--warning"
			}
		},
		displayName() {
			return this.name == this.otherClient ? `Client ${this.otherClient}` : this.name
		},
		focusContractView() {
			return this.$route.query.focus === "contract"
		},
		focusPerformanceView() {
			return this.$route.query.focus === "performance"
		},
		canViewKpi() {
			return this.$accessToOperation("KPI", "R") || this.$accessToOperation("CO", "R")
		},
		canEnterKpi() {
			const roles = this.$store.getters["user/getUser"]?.roles || []
			if (roles.length > 0) {
				const roleDecision = canEnterKpiFromRoles(roles)
				if (roleDecision === false) {
					return false
				}
				if (roleDecision === true) {
					return true
				}
			}
			return this.$accessToOperation("KPI", "U") || this.$accessToOperation("KPI", "C")
		},
		canUploadContract() {
			const roles = this.$store.getters["user/getUser"]?.roles || []
			return canUploadContractFromRoles(roles)
		},
	},
	watch: {
		"$route.query.kpiPerformance"(value) {
			if (value === "1") {
				this.openSharedKpiPerformanceDialog()
			}
		},
		"$route.params.id"() {
			if (this.focusContractView) {
				this.scheduleContractScroll()
			}
			if (this.focusPerformanceView) {
				this.schedulePerformanceScroll()
			}
		},
		connectionStatus(value) {
			if (value === "mutual" && this.focusContractView) {
				this.scheduleContractScroll()
			}
			if (value === "mutual" && this.focusPerformanceView) {
				this.schedulePerformanceScroll()
			}
		},
		kpiEvaluationHistory() {
			if (this.focusPerformanceView && this.connectionStatus === "mutual") {
				this.schedulePerformanceScroll()
			}
		},
		"recentOptions.options": {
			handler() {
				//if user select all
				if (this.recentOptions.options.itemsPerPage === -1) {
					this.recentOptions.options.itemsPerPage = this.recentOptions.total
				}
				this.recentOptions.offset = (this.recentOptions.options?.page - 1) * this.recentOptions.options?.itemsPerPage
				this.recentOptions.limit = this.recentOptions.options?.itemsPerPage
				this.getRecentTransaction()
			},
			deep: true,
		},
	},
	mounted() {
		this.otherClient = parseInt(this.$route.params.id)
		this.getConnectionInformation()
		this.dayChange(this.selectedDayCount)
		this.getRecentAudits()
		this.getRecentTransactions()
		this.viewFile(parseInt(this.$route.params.id))
		this.getRecentOnsiteAudits()
	},
	methods: {
		viewFile(id){
			FilesAPI.getFiledataByID(id)
				.then((data) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
					this.policies = data.filter(policyFile => policyFile.PolicyCertificate === 0).map(filedata => ({
						number: 0,
						fileID: filedata.FileID,
						filename: filedata.FileName,
						shortDescription: filedata.ShortDescription,
						updatedby: filedata.UserID,
						timestamp: filedata.TimeOfUpload,
					})).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
						.map((item, index) => ({
							...item,
							number: index + 1,
						}))
					this.certifications = data.filter(policyFile => policyFile.PolicyCertificate === 1).map(filedata => ({
						no: 0,
						fileID: filedata.FileID,
						filename: filedata.FileName,
						shortDescription: filedata.ShortDescription,
						updatedby: filedata.UserID,
						timestamp: filedata.TimeOfUpload,
						expiryDate: filedata.ExpiryDate
					})).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
						.map((item, index) => ({
							...item,
							no: index + 1,
						}))
				})
				.catch(error => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		downloadFiles(fileID, filename) {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				"Downloading file...",
				{ root: true }
			)
			/* let filename = this.filename(fileID) */
			FilesAPI.downloadFile("" + fileID, filename)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"File was successfully downloaded!",
						{ root: true }
					)
				})
				.catch(err => {
					console.error(err)
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						err,
						{
							root: true
						}
					)
				})
		},
		dayChange(dayCount) {
			if (dayCount) {
				this.selectedDayCount = dayCount
				this.getauditsReceived(dayCount)
				this.getauditsCompleted(dayCount)
				this.gettransactionsSent(dayCount)
				this.gettransactionsReceived(dayCount)
			}
		},
		getauditsReceived(dayCount) {
			AuditsAPI.list({
				type: "incoming",
				startDate: new Date(new Date().setDate(new Date().getDate() - dayCount)).getTime(),
				endDate: new Date().getTime(),
				otherParams: {
					counterpart: this.otherClient
				}
			})
				.then(result => {
					this.auditsReceived = result.total
				})
				.catch(/* reason */() => {
					this.auditsReceived = 0
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					) */
				})
		},
		getauditsCompleted(dayCount) {
			AuditsAPI.list({
				type: "outgoing",
				startDate: new Date(new Date().setDate(new Date().getDate() - dayCount)).getTime(),
				endDate: new Date().getTime(),
				otherParams: {
					counterpart: this.otherClient
				}
			})
				.then(result => {
					this.auditsCompleted = result.total
				})
				.catch(/* reason */() => {
					this.auditsCompleted = 0
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					) */
				})
		},
		gettransactionsSent(dayCount) {
			TransactionAPI.getTransactionWithParams({
				role: 1,
				startDate: new Date(new Date().setDate(new Date().getDate() - dayCount)).getTime(),
				endDate: new Date().getTime(),
				countOnly: true,
				counterpart: this.otherClient
			})
				.then(result => {
					this.transactionsSent = result
				})
				.catch(/* reason */() => {
					this.transactionsSent = 0
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					) */
				})
		},
		gettransactionsReceived(dayCount) {
			TransactionAPI.getTransactionWithParams({
				role: 2,
				startDate: new Date(new Date().setDate(new Date().getDate() - dayCount)).getTime(),
				endDate: new Date().getTime(),
				countOnly: true,
				counterpart: this.otherClient
			})
				.then(result => {
					this.transactionsReceived = result
				})
				.catch(/* reason */() => {
					this.transactionsReceived = 0
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					) */
				})
		},
		getRecentTransactions() {
			this.recentOptions.loading = true
			TransactionAPI.getTransactionList(this.recentOptions.limit, this.recentOptions.offset, 0, { counterpart: this.otherClient })
				.then(data => {
					this.recentTransactions = data?.data.map((t) => { t.click = null; return t })
					this.recentOptions.loading = false
				})
				.catch(/* error */() => {
					this.recentTransactions = []
					this.recentOptions.loading = false
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					) */
				})
		},
		getRecentAudits() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
 
			this.loading = true
			const { page, itemsPerPage } = this.completedaudits_options
			
			// Initialize arrays
			this.incomingAudits = []
			this.outgoingAudits = []
			
			// Track how many API calls we're making
			let pendingCalls = 4
			const checkComplete = () => {
				pendingCalls--
				if (pendingCalls === 0) {
					this.loading = false
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				}
			}
 
			// Fetch regular incoming audits (flowDown: 0)
			AuditsAPI.list({ type: "incoming", limit: itemsPerPage, offset: (page-1)*itemsPerPage, flowDown: 0, otherParams: { counterpart: this.otherClient } })
				.then(result => {
					console.log("print incoming", result)
					
					for (let entry of result.items) {
						this.incomingAudits.push({
							id: entry.id,
							flowDown: entry.allowFlowDown,
							sender: entry.senderName || entry.sender,
							recipient: entry.recipientName || entry.recipient,
							timestamp: this.$moment(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
							template: entry.templateName,
							click: null,
							status: entry.status
						})
					}
					checkComplete()
				})
				.catch(/* reason */() => {
					checkComplete()
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					) */
				})
 
			// Fetch flow-down incoming audits (flowDown: 1)
			AuditsAPI.list({ type: "incoming", limit: itemsPerPage, offset: (page-1)*itemsPerPage, flowDown: 1, otherParams: { counterpart: this.otherClient } })
				.then(result => {
					for (let entry of result.items) {
						this.incomingAudits.push({
							id: entry.id,
							flowDown: entry.allowFlowDown,
							sender: entry.senderName || entry.sender,
							recipient: entry.recipientName || entry.recipient,
							timestamp: this.$moment(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
							template: entry.templateName,
							click: null,
							status: entry.status
						})
					}
					checkComplete()
				})
				.catch(/* reason */() => {
					checkComplete()
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					) */
				})
 
			// Fetch regular outgoing audits (flowDown: 0)
			AuditsAPI.list({ type: "outgoing", limit: itemsPerPage, offset: (page-1)*itemsPerPage, flowDown: 0, otherParams: { counterpart: this.otherClient } })
				.then(result => {
					console.log("print outgoing", result)
					for (let entry of result.items) {
						this.outgoingAudits.push({
							id: entry.id,
							flowDown: entry.allowFlowDown,
							recipient: entry.recipientName || entry.recipient,
							sender: entry.senderName || entry.sender,
							timestamp: this.$moment(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
							template: entry.templateName,
							click: null,
							status: entry.status
						})
					}
					checkComplete()
				})
				.catch(/* reason */() => {
					checkComplete()
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					) */
				})
 
			// Fetch flow-down outgoing audits (flowDown: 1)
			AuditsAPI.list({ type: "outgoing", limit: itemsPerPage, offset: (page-1)*itemsPerPage, flowDown: 1, otherParams: { counterpart: this.otherClient } })
				.then(result => {
					for (let entry of result.items) {
						this.outgoingAudits.push({
							id: entry.id,
							flowDown: entry.allowFlowDown,
							recipient: entry.recipientName || entry.recipient,
							sender: entry.senderName || entry.sender,
							timestamp: this.$moment(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
							template: entry.templateName,
							click: null,
							status: entry.status
						})
					}
					checkComplete()
				})
				.catch(/* reason */() => {
					checkComplete()
					/* this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					) */
				})
		},
		getRecentOnsiteAudits() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)

			this.loading = true
			const { page, itemsPerPage } = this.completedaudits_options
			AuditsAPI.onsiteList({ type: "incoming", offset: (page-1)*itemsPerPage, limit: itemsPerPage })
				.then(result => {
					this.incomingOnsiteAudits = []
					for (let entry of result.items) {
						this.incomingOnsiteAudits.push({
							id: entry.id,
							sender: entry.senderName,
							timestamp: this.$moment(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
							template: entry.templateName,
							recipient: entry.supplierName,
							onsite: entry.allowOnsite,
							auditorType: entry.auditorType,
							supplier: entry.supplier,
							click: null
						})
						
					}
					this.loading = false
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch(() => {
					this.loading = false
					this.incomingOnsiteAudits = []
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						null,
						{ root: true }
					)
				})
		},
		getConnectionInformation() {
			ConnectionsAPI.getConnectionInformation(this.otherClient)
				.then(result => {
					this.name = result.name
					this.otherClient = result.clientID
					this.connectionStatus = result.status
					this.logoURL = result.logoURL
					if (this.connectionStatus === "mutual") {
						const loadTasks = [this.getContract(), this.getCsr(this.otherClient)]
						Promise.all(loadTasks).then(() => {
							if (this.focusContractView) {
								this.scheduleContractScroll()
							}
						})
						if (this.canViewKpi) {
							this.getKpiEvaluation()
							this.getKpiEvaluationHistory()
						}
						if (this.$route.query.kpiPerformance === "1") {
							this.openSharedKpiPerformanceDialog()
						}
					}
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch(reason => {
					this.loading = false
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					)
				})
		},
		auditRowClicked(data) {
			if(this.onsite == 1){
				this.$router.push({ name: `Audit Onsite Details`, params: { id: data.id } })
			}else{
				this.$router.push({ name: `Audit Details`, params: { id: data.id } })
			}
			
		},
		transactionRowClicked(data) {
			this.$router.push({ name: `Transaction Details`, params: { id: data.id } })
		},
		connectionStatusString(companyName) {
			switch (this.connectionStatus) {
			case "mutual":
				return `You are connected with ${companyName}!`
			case "incoming":
				return `${companyName} has sent you a connection request!`
			case "outgoing":
				return `You have sent ${companyName} a connection request!`
			case "none":
				return `You are not connected to ${companyName}.`
			}
		},
		getScore(scoreNumber) {
			if (!this.supplier || !this.supplier.csr) {
				return null
			}
			switch (scoreNumber) {
			case 1:
				return this.supplier.csr.score1
			case 2:
				return this.supplier.csr.score2
			case 3:
				return this.supplier.csr.score3
			case 4:
				return this.supplier.csr.score4
			}
		},
		getCsr(id) {
			return SupplierAPI.getSupplier(id)
				.then((result) => {
					this.supplier = result
				})
				.catch((error) => {
					console.log(error)
				})
		},
		getButtonColor(status) {
			switch (status) {
			case 0:
				return "primary"
			case 1:
				return "success"
			case 2:
				return "warning"
			case 3:
				return "error"
			default:
				return "black"
			}},
		showContractUploadError(message) {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_SET_ERROR,
				new Error(message),
				{ root: true }
			)
		},
		uploadContract(payload) {
			this.contractUploading = true
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				"Uploading contract...",
				{ root: true }
			)
			ConnectionsAPI.uploadContract(this.otherClient, payload)
				.then(() => this.getContract())
				.then(() => {
					const message = payload.isReplace && !payload.file
						? "Contract updated successfully!"
						: payload.isReplace
							? "Contract replaced successfully!"
							: "Contract uploaded successfully!"
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						message,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
				.finally(() => {
					this.contractUploading = false
				})
		},
		mapContract(result) {
			if (!result) {
				return null
			}
			return {
				id: result.id,
				fileID: result.fileID,
				filename: result.filename,
				contractName: result.contractName,
				contactName: result.contactName,
				pointOfContact: result.pointOfContact,
				expiryDate: result.expiryDate,
				uploadedAt: result.uploadedAt,
			}
		},
		mapContracts(results) {
			if (!Array.isArray(results)) {
				return []
			}
			return results.map((result) => this.mapContract(result)).filter(Boolean)
		},
		getContract() {
			return ConnectionsAPI.getContract(this.otherClient)
				.then(({ contracts }) => {
					this.contracts = this.mapContracts(contracts)
				})
				.catch(() => {
					this.contracts = []
				})
		},
		scheduleContractScroll() {
			const tryScroll = (attempt = 0) => {
				this.$nextTick(() => {
					const section = this.$refs.contractStatusSection
					if (section) {
						this.scrollToContractSection(section)
						return
					}
					if (attempt < 20) {
						window.setTimeout(() => tryScroll(attempt + 1), 100)
					}
				})
			}
			window.setTimeout(() => tryScroll(), 250)
		},
		scrollToContractSection(sectionEl) {
			const section = sectionEl || this.$refs.contractStatusSection
			if (!section) {
				return
			}
			const headerOffset = 100
			const top = section.getBoundingClientRect().top + window.pageYOffset - headerOffset
			window.scrollTo({
				top: Math.max(0, top),
				behavior: "smooth",
			})
		},
		schedulePerformanceScroll() {
			const tryScroll = (attempt = 0) => {
				this.$nextTick(() => {
					const section = this.$refs.supplierPerformanceSection
					if (section) {
						this.scrollToPerformanceSection(section)
						return
					}
					if (attempt < 20) {
						window.setTimeout(() => tryScroll(attempt + 1), 100)
					}
				})
			}
			window.setTimeout(() => tryScroll(), 250)
		},
		scrollToPerformanceSection(sectionEl) {
			const section = sectionEl || this.$refs.supplierPerformanceSection
			if (!section) {
				return
			}
			const headerOffset = 100
			const top = section.getBoundingClientRect().top + window.pageYOffset - headerOffset
			window.scrollTo({
				top: Math.max(0, top),
				behavior: "smooth",
			})
		},
		getKpiEvaluation() {
			ConnectionsAPI.getKpiEvaluation(this.otherClient)
				.then((result) => {
					this.kpiEvaluation = result ? this.mapKpiEvaluation(result) : null
				})
				.catch(() => {
					this.kpiEvaluation = null
				})
		},
		getKpiEvaluationHistory() {
			return ConnectionsAPI.getKpiEvaluationHistory(this.otherClient)
				.then((results) => {
					this.kpiEvaluationHistory = (results || []).map((result) => this.mapKpiEvaluation(result))
				})
				.catch(() => {
					this.kpiEvaluationHistory = []
				})
		},
		openSharedKpiPerformanceDialog() {
			if (this.$route.query.kpiPerformance !== "1") {
				return
			}
			ConnectionsAPI.getSharedKpiEvaluation(this.otherClient)
				.then((result) => {
					if (!result) {
						this.clearKpiPerformanceQuery()
						return
					}
					this.kpiPerformanceSummary = this.mapKpiEvaluation(result)
					this.showKpiPerformanceDialog = true
					this.clearKpiPerformanceQuery()
				})
				.catch(() => {
					this.clearKpiPerformanceQuery()
				})
		},
		clearKpiPerformanceQuery() {
			if (this.$route.query.kpiPerformance !== "1") {
				return
			}
			const query = { ...this.$route.query }
			delete query.kpiPerformance
			this.$router.replace({ query }).catch(() => {})
		},
		saveKpiEvaluation(payload) {
			this.kpiSaving = true
			const messages = {
				draft: "KPI evaluation draft saved.",
				review: "KPI evaluation saved successfully!",
				share: "KPI evaluation shared with supplier!",
			}
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				"Saving KPI evaluation...",
				{ root: true }
			)
			ConnectionsAPI.saveKpiEvaluation(this.otherClient, payload)
				.then((result) => {
					const mapped = this.mapKpiEvaluation(result)
					if (payload.action === "draft") {
						this.kpiEvaluation = mapped
					} else {
						this.kpiEvaluation = null
						if (mapped) {
							this.kpiEvaluationHistory = [
								mapped,
								...this.kpiEvaluationHistory.filter((item) => item.id !== mapped.id),
							]
						}
					}
					this.getKpiEvaluationHistory()
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						messages[payload.action] || "KPI evaluation saved successfully!",
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
				.finally(() => {
					this.kpiSaving = false
				})
		},
		mapKpiEvaluation(result) {
			if (!result) {
				return null
			}
			return {
				id: result.id,
				evaluationDate: result.evaluationDate,
				deliveryKpi: result.deliveryKpi,
				qualityKpi: result.qualityKpi,
				supportKpi: result.supportKpi,
				comment: result.comment,
				status: result.status,
				createdAt: result.createdAt,
				updatedAt: result.updatedAt,
				sharedAt: result.sharedAt,
			}
		},
		showKpiAccessDenied() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_SET_ERROR,
				"You don't have access to enter KPI values.",
				{ root: true }
			)
		},
		openKpiEvaluation() {
			if (!this.canEnterKpi) {
				this.showKpiAccessDenied()
				return
			}
			if (this.$refs.contractStatus) {
				this.$refs.contractStatus.openKpiDialog()
			}
		},
		downloadContract(contract) {
			if (!contract?.fileID) {
				return
			}
			const downloadName = (contract.filename || "contract").replace(/\.pdf$/i, "")
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				"Downloading contract...",
				{ root: true }
			)
			FilesAPI.download("" + contract.fileID, downloadName)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Contract downloaded successfully!",
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
	}
}
</script>

<style scoped>
.single-client-view {
	padding-bottom: 32px;
}

.single-client-section {
	border-radius: 10px;
	background: #fff;
}

.activity-table {
	background: #e8e8e8;
	border-radius: 8px;
}

.activity-table td {
	border-bottom: thin solid rgba(0, 0, 0, 0.08) !important;
}

.activity-table tr:last-child td {
	border-bottom: none !important;
}

.activity-value {
	text-align: right;
	white-space: nowrap;
	width: 48px;
}

.activity-stats-row {
	width: 100%;
}

.section-subtitle {
	color: #666;
}

.section-break {
	height: 20px;
	background-color: #eeeeee;
}

#contract-status-section {
	scroll-margin-top: 100px;
}

#supplier-performance-section {
	scroll-margin-top: 100px;
}

.connection-status-banner {
	border-radius: 6px;
	font-size: 1.1rem;
	font-weight: 500;
	width: 50%;
	min-width: 280px;
	max-width: 100%;
	margin: 0 auto;
	display: block;
	text-align: center;
}

.connection-status-banner--success {
	background-color: #4caf50;
}

.connection-status-banner--warning {
	background-color: #fb8c00;
}

.connection-status-banner--error {
	background-color: #e53935;
}
</style>